import React from 'react';
import PropTypes from 'prop-types';
import { PadContainer } from 'smartsuite-ui';
import { orderBy } from 'natural-orderby';
import NationalSummary from './NationalSummary';
import NationalKpiListItem from './NationalKpiListItem';
import WidgetErrorMessage from '../../../WidgetErrorMessage';
import {
    getMetricSourceForRequest,
    hasMetricSources,
    allMetricSourcesAuthorized,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../../../utils/wrapperUtils';

/* eslint-disable complexity */
function getMetricSource({ metricRequests, metrics, configuration }) {
    const summaryRequest = metricRequests.find(
        (request) => request.id === configuration.summaryRequestId
    );

    const kpisRequest = metricRequests.find(
        (request) => request.id === configuration.kpisRequestId
    );

    if (
        !summaryRequest ||
        !summaryRequest.metrics?.length ||
        !kpisRequest ||
        !kpisRequest.metrics?.length
    ) {
        return { errorKey: 'invalidMetricRequests' };
    }

    const summarySource = getMetricSourceForRequest(metrics, summaryRequest);
    const kpisSource = getMetricSourceForRequest(metrics, kpisRequest);
    if (!summarySource || !kpisSource) {
        return { errorKey: 'metricSourceNotFound' };
    }

    if (!allMetricSourcesAuthorized([summarySource, kpisSource])) {
        return { errorKey: 'metricSourceUnauthorized' };
    }

    if (!hasMetricSources([summarySource, kpisSource], false)) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        [summarySource, kpisSource],
        metricRequests
    );

    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { summaryRequest, kpisRequest, summarySource, kpisSource };
}

export default function NationalKpisWrapper(props) {
    const {
        summarySource,
        kpisSource,
        kpisRequest,
        metricSourceErrors,
        errorKey,
    } = getMetricSource(props);

    if (errorKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const summaryData = summarySource.metricSeries[0];
    const kpiData = kpisSource.metricSeries;

    return (
        <div className="chart-container">
            <NationalSummary data={summaryData} />
            <PadContainer cssClassModifiers={['pad-medium']} />
            <div className="national-kpi-list">
                {orderBy(kpiData, 'sortOrder').map((item) => (
                    <NationalKpiListItem
                        key={item.kpiName}
                        item={item}
                        linkConfig={props.configuration.linkConfig}
                        metricRequest={kpisRequest}
                        openLinkInNewTab={props.configuration.openLinkInNewTab}
                    />
                ))}
            </div>
        </div>
    );
}
/* eslint-enable complexity */

NationalKpisWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
};
